import { useMediaQuery, useTheme } from '@mui/material'
import dayjs from 'dayjs'
import duration from 'dayjs/plugin/duration'
import { useEffect, useState } from 'react'

dayjs.extend(duration)

type TimerValue = {
    days: number
    hours: number
    minutes: number
    seconds: number
}

type CountdownTimerState = {
    timer: TimerValue
    isComplete: boolean
    isLastHour: boolean
    text: string
}

const getFormattedString = ({ days, hours, minutes, seconds }: TimerValue, isMobile?: boolean) => {
    const minutesEnding = isMobile ? 'м' : 'мин'
    const secondsEnding = isMobile ? 'с' : 'сек'

    if (days > 0) {
        return `${days} д ${hours} ч`
    }

    if (hours > 0) {
        return `${hours} ч ${minutes} ${minutesEnding}`
    }

    return `${minutes} ${minutesEnding} ${seconds} ${secondsEnding}`
}

export const useCountdownTimer = (endDate: string | null): CountdownTimerState => {
    const { breakpoints } = useTheme()
    const isMobile = useMediaQuery(breakpoints.down('md'))

    const [state, setState] = useState<CountdownTimerState>({
        timer: { days: 0, hours: 0, minutes: 0, seconds: 0 },
        isComplete: false,
        isLastHour: false,
        text: '',
    })

    useEffect(() => {
        let timerId: NodeJS.Timeout

        if (endDate) {
            const computeTimerValues = () => {
                const diffMs = dayjs(endDate).diff(dayjs())

                const remainingDuration = dayjs.duration(diffMs)

                const days = Math.max(0, remainingDuration.days())
                const hours = Math.max(0, remainingDuration.hours())
                const minutes = Math.max(0, remainingDuration.minutes())
                const seconds = Math.max(0, remainingDuration.seconds())

                const isComplete = !days && !hours && !minutes && !seconds

                setState({
                    timer: {
                        days,
                        hours,
                        minutes,
                        seconds,
                    },
                    isComplete,
                    isLastHour: !days && !hours,
                    text: getFormattedString({ days, hours, minutes, seconds }, isMobile),
                })

                if (isComplete) {
                    clearInterval(timerId)
                }
            }

            computeTimerValues()

            timerId = setInterval(computeTimerValues, 1000)
        }

        return () => {
            clearInterval(timerId)
        }
    }, [endDate, isMobile])

    return state
}
